import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Brain, Mail, Loader2, CheckCircle, ArrowLeft } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResendVerification = ({ initialEmail = '' }) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState(initialEmail);
  const [status, setStatus] = useState('form'); // form | loading | sent | error
  const [message, setMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setMessage('');
    try {
      const resp = await fetch(`${API}/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || 'Failed to send verification email');
      setMessage(data.message || 'Verification email sent. Check your inbox.');
      setStatus('sent');
    } catch (err) {
      setMessage(err.message);
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-600 to-cyan-500 flex items-center justify-center mx-auto mb-4">
            <Brain className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-purple-400 via-cyan-400 to-purple-400 bg-clip-text text-transparent mb-2">
            NeuroWave One
          </h1>
          <p className="text-white/50 text-sm">Advanced Brainwave Entrainment</p>
        </div>

        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 sm:p-8">
          <button
            onClick={() => navigate('/app')}
            className="flex items-center gap-1 text-white/40 hover:text-white/60 text-sm mb-4"
            data-testid="resend-back-btn"
          >
            <ArrowLeft className="w-4 h-4" /> Back to sign in
          </button>

          {status === 'sent' ? (
            <div className="text-center" data-testid="resend-success">
              <div className="w-14 h-14 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-7 h-7 text-emerald-400" />
              </div>
              <h2 className="text-white text-lg font-semibold mb-2">Check your email</h2>
              <p className="text-white/50 text-sm mb-4">{message}</p>
              <p className="text-white/30 text-xs">The link expires in 24 hours.</p>
              <Button
                onClick={() => navigate('/app')}
                className="mt-6 w-full bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-700 hover:to-cyan-700 text-white py-5 rounded-xl font-semibold text-sm"
                data-testid="resend-go-signin"
              >
                Back to sign in
              </Button>
            </div>
          ) : (
            <>
              <div className="text-center mb-6">
                <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center mx-auto mb-3">
                  <Mail className="w-6 h-6 text-purple-400" />
                </div>
                <h2 className="text-white text-lg font-semibold mb-2" data-testid="resend-title">
                  Resend verification email
                </h2>
                <p className="text-white/40 text-xs">
                  Didn't receive the email or the link expired? Enter your address and we'll send a new one.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-3">
                <input
                  type="email"
                  placeholder="Email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 text-white placeholder-white/30 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-purple-500/50"
                  data-testid="resend-email-input"
                  required
                />
                {status === 'error' && (
                  <p className="text-red-400 text-xs" data-testid="resend-error">{message}</p>
                )}
                <Button
                  type="submit"
                  disabled={status === 'loading'}
                  className="w-full bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-700 hover:to-cyan-700 text-white py-5 rounded-xl font-semibold text-sm"
                  data-testid="resend-submit-btn"
                >
                  {status === 'loading' ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Send verification email'
                  )}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResendVerification;
